import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import SearchableSelect from "../components/SearchableSelect";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import StickyPageHeader from "../components/StickyPageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Wallet, Download, CheckCircle2, RotateCcw, Pencil, X, Save, Receipt, Calendar,
  TrendingUp, ChevronRight, AlertCircle, ShieldCheck, Info,
  ArrowUpDown, ArrowDownCircle, ArrowUpCircle, BanknoteArrowDown,
} from "lucide-react";
import { toast } from "sonner";

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const previousMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
// Conservé pour usage futur (raccourci « mois précédent »).
const _prev = previousMonth; void _prev;
const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1)
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};
const money = (v) => {
  if (v === null || v === undefined) return "—";
  const raw = Number(v).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Force les espaces normales (fr-FR retourne U+202F narrow-no-break qui
  // ne s'affiche pas correctement en font-mono).
  return `${raw.replace(/\u202f|\u00a0/g, " ")} €`;
};

export default function BulletinsRemuneration() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [month, setMonth] = useState(currentMonth());
  const [brandFilter, setBrandFilter] = useState(() => selectedBrandId || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // objet statement affiché en modale

  const isAdmin = user?.role === "admin";

  useEffect(() => { setBrandFilter(selectedBrandId || ""); }, [selectedBrandId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (brandFilter) params.brand_id = brandFilter;
      const { data } = await api.get("/remuneration-statements", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [month, brandFilter]);

  useEffect(() => { load(); }, [load]);

  const drafts = useMemo(() => items.filter((i) => i.status !== "validated"), [items]);
  const validated = useMemo(() => items.filter((i) => i.status === "validated"), [items]);

  const totalDraft = useMemo(() => drafts.reduce((s, i) => s + (i.total_ht || 0), 0), [drafts]);
  const totalValidated = useMemo(() => validated.reduce((s, i) => s + (i.total_ht || 0), 0), [validated]);

  const openDetail = async (row) => {
    try {
      const { data } = await api.get(`/remuneration-statements/${row.collaborateur_id}/${month}`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    const { data } = await api.get(`/remuneration-statements/${detail.collaborateur_id}/${month}`);
    setDetail(data);
    load();
  };

  const downloadPdf = async (cid, m) => {
    try {
      const res = await api.get(`/remuneration-statements/${cid}/${m}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `remuneration_${cid}_${m}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'export PDF");
    }
  };

  if (user && !["admin", "manager"].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6" data-testid="bulletins-remuneration-page">
      <StickyPageHeader testid="bulletins-remuneration-sticky-header">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="label-uppercase mb-2">Suivi paie</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
              Bulletins de rémunération
            </h1>
            <p className="text-sm text-zinc-600 mt-1 font-mono">
              Génération automatique + validation admin des relevés mensuels par collaborateur.
            </p>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-3 flex flex-wrap gap-3 items-end">
          <div className="min-w-[180px]">
            <Label className="label-uppercase mb-1.5 block">Mois de référence</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="remun-month-input"
            />
          </div>
          <div className="min-w-[240px]">
            <Label className="label-uppercase mb-1.5 block">Département</Label>
            <SearchableSelect
              value={brandFilter || ""}
              onChange={(v) => setBrandFilter(v)}
              options={brands.map((b) => ({ value: b.id, label: b.name }))}
              placeholder="Tous les départements"
              searchPlaceholder="Rechercher un département…"
              emptyLabel="Aucun département trouvé."
              allowClear
              data-testid="remun-brand-filter"
            />
          </div>
          <div className="ml-auto flex gap-3">
            <MiniStat icon={<Receipt size={14} />} label="En cours" value={drafts.length} sub={money(totalDraft)} tid="stat-draft" />
            <MiniStat icon={<ShieldCheck size={14} />} label="Validés" value={validated.length} sub={money(totalValidated)} accent="#10B981" tid="stat-validated" />
          </div>
        </div>
      </StickyPageHeader>

      <Tabs defaultValue="draft" className="w-full">
        <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-max">
            <TabsTrigger value="draft" data-testid="tab-drafts">
              <Pencil size={13} className="mr-1.5" /> Relevés en cours ({drafts.length})
            </TabsTrigger>
            <TabsTrigger value="validated" data-testid="tab-validated">
              <ShieldCheck size={13} className="mr-1.5" /> Relevés validés ({validated.length})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="draft" className="mt-4">
          <StatementsTable
            rows={drafts}
            loading={loading}
            isAdmin={isAdmin}
            onOpenDetail={openDetail}
            onDownloadPdf={downloadPdf}
            month={month}
            emptyLabel={`Aucun relevé à traiter pour ${monthLabel(month)}.`}
          />
        </TabsContent>
        <TabsContent value="validated" className="mt-4">
          <StatementsTable
            rows={validated}
            loading={loading}
            isAdmin={isAdmin}
            onOpenDetail={openDetail}
            onDownloadPdf={downloadPdf}
            month={month}
            emptyLabel={`Aucun relevé validé pour ${monthLabel(month)}.`}
          />
        </TabsContent>
      </Tabs>

      <StatementDetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        detail={detail}
        month={month}
        isAdmin={isAdmin}
        onRefresh={refreshDetail}
        onDownloadPdf={downloadPdf}
      />
    </div>
  );
}

/* ─── Mini stat card ────────────────────────────────────────────────────── */
function MiniStat({ icon, label, value, sub, accent = "#0055FF", tid }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-sm px-4 py-2.5 inline-flex items-center gap-3" data-testid={tid}>
      <span className="h-7 w-7 rounded-sm text-white inline-flex items-center justify-center" style={{ backgroundColor: accent }}>
        {icon}
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</p>
        <p className="text-lg font-display font-bold text-zinc-900 leading-tight">{value}</p>
        <p className="text-[10px] font-mono text-zinc-500">{sub}</p>
      </div>
    </div>
  );
}

/* ─── Table des relevés ─────────────────────────────────────────────────── */
function StatementsTable({ rows, loading, isAdmin, onOpenDetail, onDownloadPdf, month, emptyLabel }) {
  if (loading) {
    return <div className="bg-white border border-zinc-200 rounded-sm p-10 text-center font-mono text-sm text-zinc-500">Chargement…</div>;
  }
  if (!rows.length) {
    return (
      <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-10 text-center" data-testid="statements-empty">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-zinc-100 mb-2">
          <Wallet size={18} className="text-zinc-500" />
        </div>
        <p className="text-sm text-zinc-700">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
      <HorizontalScrollContainer testId="statements-scroll">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="text-zinc-500 text-[10px] uppercase tracking-wider bg-zinc-50">
            <tr>
              <th className="text-left px-4 py-2">Collaborateur</th>
              <th className="text-left px-4 py-2">Poste</th>
              <th className="text-left px-4 py-2">N° ID immat.</th>
              <th className="text-right px-4 py-2">Fixe prorata</th>
              <th className="text-right px-4 py-2">Unitaire</th>
              <th className="text-right px-4 py-2">Taux %</th>
              <th className="text-right px-4 py-2">Total HT</th>
              <th className="text-center px-4 py-2">Statut</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.collaborateur_id}
                className="border-t border-zinc-100 hover:bg-zinc-50"
                data-testid={`statement-row-${r.collaborateur_id}`}
              >
                <td className="px-4 py-2.5 font-medium text-zinc-900">{r.collaborateur_name}</td>
                <td className="px-4 py-2.5 text-xs text-zinc-600">{r.collaborateur_poste || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-zinc-700" data-testid={`row-immat-${r.collaborateur_id}`}>
                  {r.collaborateur_id_immatriculation || "—"}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">{money(r.fixed_amount_prorated)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{money(r.variable_unit_total)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{money(r.variable_rate_amount)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-semibold">{money(r.total_ht)}</td>
                <td className="px-4 py-2.5 text-center">
                  {r.status === "validated" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-0" data-testid={`badge-validated-${r.collaborateur_id}`}>
                      <ShieldCheck size={11} className="mr-1" /> Validé
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid={`badge-draft-${r.collaborateur_id}`}>
                      Prévisionnel
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="mr-1.5"
                    onClick={() => onDownloadPdf(r.collaborateur_id, month)}
                    data-testid={`btn-pdf-${r.collaborateur_id}`}
                  >
                    <Download size={12} className="mr-1" /> PDF
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onOpenDetail(r)}
                    className="bg-[#0055FF] hover:bg-[#0044CC]"
                    data-testid={`btn-detail-${r.collaborateur_id}`}
                  >
                    <ChevronRight size={12} className="mr-1" /> Détail
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </HorizontalScrollContainer>
    </div>
  );
}

/* ─── Modale de détail ──────────────────────────────────────────────────── */
function StatementDetailDialog({ open, onClose, detail, month, isAdmin, onRefresh, onDownloadPdf }) {
  const [caInput, setCaInput] = useState("");
  const [editingCa, setEditingCa] = useState(false);
  const [bonusInput, setBonusInput] = useState("");
  const [editingBonus, setEditingBonus] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingBonus, setSavingBonus] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  // Rubrique 5 — Réajustement
  const [editingAdj, setEditingAdj] = useState(false);
  const [savingAdj, setSavingAdj] = useState(false);
  const [adjType, setAdjType] = useState("manque_a_gagner");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjComment, setAdjComment] = useState("");
  // Rubrique 6 — Avance / acompte
  const [editingAdv, setEditingAdv] = useState(false);
  const [savingAdv, setSavingAdv] = useState(false);
  const [advAmount, setAdvAmount] = useState("");
  const [advDate, setAdvDate] = useState("");
  const [advMethod, setAdvMethod] = useState("virement");

  useEffect(() => {
    if (detail) {
      setCaInput(detail.variable_rate_ca_manual ?? "");
      setEditingCa(false);
      setBonusInput(detail.bonus_amount ? String(detail.bonus_amount) : "");
      setEditingBonus(false);
      // Réajustement
      setAdjType(detail.adjustment_type || "manque_a_gagner");
      setAdjAmount(detail.adjustment_amount ? String(detail.adjustment_amount) : "");
      setAdjComment(detail.adjustment_comment || "");
      setEditingAdj(false);
      // Avance
      setAdvAmount(detail.advance_amount ? String(detail.advance_amount) : "");
      setAdvDate(detail.advance_date || "");
      setAdvMethod(detail.advance_method || "virement");
      setEditingAdv(false);
    }
  }, [detail]);

  if (!detail) return null;
  const isValidated = detail.status === "validated";
  const isRateMode = detail.variable_mode === "percentage";

  const saveCa = async () => {
    if (isValidated) return;
    setSaving(true);
    try {
      const val = caInput === "" || caInput === null ? null : Number(caInput);
      await api.patch(`/remuneration-statements/${detail.collaborateur_id}/${month}`, {
        variable_rate_ca: val,
      });
      toast.success(val === null ? "Calcul automatique rétabli" : "Correction manuelle enregistrée");
      setEditingCa(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const resetManualCa = async () => {
    if (isValidated) return;
    setSaving(true);
    try {
      await api.patch(`/remuneration-statements/${detail.collaborateur_id}/${month}`, {
        variable_rate_ca: null,
      });
      toast.success("Calcul automatique rétabli");
      setCaInput("");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const saveBonus = async () => {
    if (isValidated) return;
    setSavingBonus(true);
    try {
      const raw = bonusInput === "" || bonusInput === null ? null : Number(bonusInput);
      if (raw !== null && (Number.isNaN(raw) || raw < 0)) {
        toast.error("Le bonus doit être un montant positif");
        return;
      }
      await api.patch(`/remuneration-statements/${detail.collaborateur_id}/${month}`, {
        bonus_amount: raw,
      });
      toast.success(raw ? "Bonus exceptionnel enregistré" : "Bonus retiré");
      setEditingBonus(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingBonus(false);
    }
  };

  const saveAdjustment = async () => {
    if (isValidated) return;
    const amt = adjAmount === "" ? 0 : Number(adjAmount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error("Le montant du réajustement doit être ≥ 0.");
      return;
    }
    if (amt > 0 && !adjComment.trim()) {
      toast.error("Un commentaire explicatif est requis.");
      return;
    }
    setSavingAdj(true);
    try {
      await api.patch(`/remuneration-statements/${detail.collaborateur_id}/${month}`, {
        adjustment_amount: amt,
        adjustment_type: amt > 0 ? adjType : null,
        adjustment_comment: amt > 0 ? adjComment.trim() : null,
      });
      toast.success(amt > 0 ? "Réajustement enregistré" : "Réajustement retiré");
      setEditingAdj(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingAdj(false);
    }
  };

  const removeAdjustment = async () => {
    if (isValidated) return;
    setSavingAdj(true);
    try {
      await api.patch(`/remuneration-statements/${detail.collaborateur_id}/${month}`, {
        adjustment_amount: 0,
        adjustment_type: null,
        adjustment_comment: null,
      });
      toast.success("Réajustement retiré");
      setAdjAmount("");
      setAdjComment("");
      setEditingAdj(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingAdj(false);
    }
  };

  const saveAdvance = async () => {
    if (isValidated) return;
    const amt = advAmount === "" ? 0 : Number(advAmount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error("Le montant de l'avance doit être ≥ 0.");
      return;
    }
    if (amt > 0 && !advDate) {
      toast.error("La date de versement est requise.");
      return;
    }
    if (amt > 0 && !advMethod) {
      toast.error("Le mode de versement est requis.");
      return;
    }
    setSavingAdv(true);
    try {
      await api.patch(`/remuneration-statements/${detail.collaborateur_id}/${month}`, {
        advance_amount: amt,
        advance_date: amt > 0 ? advDate : null,
        advance_method: amt > 0 ? advMethod : null,
      });
      toast.success(amt > 0 ? "Avance/acompte enregistré" : "Avance retirée");
      setEditingAdv(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingAdv(false);
    }
  };

  const removeAdvance = async () => {
    if (isValidated) return;
    setSavingAdv(true);
    try {
      await api.patch(`/remuneration-statements/${detail.collaborateur_id}/${month}`, {
        advance_amount: 0,
        advance_date: null,
        advance_method: null,
      });
      toast.success("Avance/acompte retiré");
      setAdvAmount("");
      setAdvDate("");
      setEditingAdv(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingAdv(false);
    }
  };

  const validate = async () => {
    setConfirming(true);
    try {
      await api.post(`/remuneration-statements/${detail.collaborateur_id}/${month}/validate`);
      toast.success("Relevé validé — le collaborateur peut le consulter dans « Ma rémunération »");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setConfirming(false);
    }
  };

  const reopen = async () => {
    setReopenOpen(false);
    try {
      await api.post(`/remuneration-statements/${detail.collaborateur_id}/${month}/reopen`);
      toast.success("Relevé rouvert");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="statement-detail-dialog">
        <DialogHeader>
          <DialogTitle>Relevé de rémunération — {detail.collaborateur_name}</DialogTitle>
          <DialogDescription>
            Période : {monthLabel(month)} — Statut :{" "}
            {isValidated ? (
              <span className="text-emerald-700 font-semibold">
                Validé ({detail.validated_at ? new Date(detail.validated_at).toLocaleDateString("fr-FR") : "—"})
              </span>
            ) : (
              <span className="text-amber-700 font-semibold">Prévisionnel</span>
            )}
            {detail.collaborateur_id_immatriculation ? (
              <>
                {" "}— N° ID immat. :{" "}
                <span className="font-mono text-zinc-700" data-testid="detail-id-immatriculation">
                  {detail.collaborateur_id_immatriculation}
                </span>
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Bloc 1 : Fixe */}
          <SectionCard title="1. Base forfaitaire mensuelle" icon={<Wallet size={14} />} accent="#10B981">
            <KV label="Montant forfaitaire mensuel HT" value={money(detail.fixed_ht_config)} />
            <KV label="Jours calendaires du mois" value={detail.calendar_days} />
            <KV
              label="Jours ouvrables du mois (hors dim./fériés)"
              value={detail.working_days_in_month}
              tid="detail-working-days-in-month"
            />
            <KV label="Jours d'exécution du contrat" value={detail.exec_days} tid="detail-exec-days" />
            <KV label="Jours retirés (absence / non effectué)" value={detail.absent_days} />
            <KV
              label="Dimanches/fériés travaillés (bonus +5 %)"
              value={detail.dow_bonus_days}
              tid="detail-dow-bonus-days"
            />
            <KV label="Jours travaillés retenus (ouvrables)" value={detail.worked_days} tid="detail-worked-days" />
            <KV label="Shifts planifiés (déclencheur)" value={detail.shifts_planned} />
            <div className="col-span-2 border-t border-zinc-200 pt-2 mt-2 flex justify-between font-mono">
              <span className="font-semibold text-zinc-700">Forfait mensuel HT</span>
              <span className="font-bold text-zinc-900" data-testid="detail-fixed-total">{money(detail.fixed_amount_prorated)}</span>
            </div>
            {detail.shifts_planned === 0 && (
              <div className="col-span-2 mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs text-amber-800" data-testid="warning-no-plan">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>Aucune planification sur la période : le collaborateur est considéré <b>indisponible</b>. Jours d&apos;exécution et jours travaillés retenus = 0.</span>
              </div>
            )}
          </SectionCard>

          {/* Bloc 2 : Variable unitaire */}
          <SectionCard title="2. Variable — commission unitaire par vente" icon={<TrendingUp size={14} />} accent="#F97316">
            {detail.variable_mode === "amount" ? (
              <>
                <KV label="Prime unitaire HT / vente" value={money(detail.variable_amount_unit)} />
                <KV label="Nombre de transactions" value={detail.transactions?.length || 0} />
                {(detail.transactions || []).length > 0 && (
                  <div className="col-span-2 border border-zinc-200 rounded-sm overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50 text-zinc-500 uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-1.5">Date</th>
                          <th className="text-left px-3 py-1.5">Service</th>
                          <th className="text-right px-3 py-1.5">Qté</th>
                          <th className="text-right px-3 py-1.5">Vente TTC</th>
                          <th className="text-right px-3 py-1.5">Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.transactions || []).map((t) => (
                          <tr key={t.transaction_id} className="border-t border-zinc-100">
                            <td className="px-3 py-1.5 font-mono">{t.date}</td>
                            <td className="px-3 py-1.5">{t.service || "—"}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{t.quantity}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{money(t.amount_ttc)}</td>
                            <td className="px-3 py-1.5 text-right font-mono font-semibold">{money(t.commission)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="col-span-2 border-t border-zinc-200 pt-2 mt-2 flex justify-between font-mono">
                  <span className="font-semibold text-zinc-700">Total HT</span>
                  <span className="font-bold text-zinc-900" data-testid="detail-unit-total">{money(detail.variable_unit_total)}</span>
                </div>
              </>
            ) : (
              <p className="col-span-2 text-xs text-zinc-500 italic">Non applicable — le mode variable configuré n&apos;est pas « par vente ».</p>
            )}
          </SectionCard>

          {/* Bloc 3 : Variable taux */}
          <SectionCard title="3. Variable — pourcentage du CA" icon={<TrendingUp size={14} />} accent="#0055FF">
            {isRateMode ? (
              <>
                <KV label="Taux appliqué" value={`${Number(detail.variable_percentage || 0).toFixed(2)} %`} />
                <KV label="CA net auto (départements)" value={money(detail.variable_rate_ca_auto || 0)} tid="row-ca-auto" />

                {detail.variable_rate_prorata_applied && !editingCa && (
                  <KV
                    tid="row-ca-prorata"
                    label={`Prorata (${detail.worked_days || 0}/${detail.working_days_in_month || 0} j. ouvrables)`}
                    value={money(detail.variable_rate_ca_prorated || 0)}
                  />
                )}

                {/* Ligne "CA de référence retenu" + actions admin ------------------- */}
                <div className="col-span-2 flex items-center justify-between mt-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
                      CA de référence retenu
                    </span>
                    {detail.variable_rate_ca_source === "manual" && (
                      <span
                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200"
                        data-testid="badge-ca-manual"
                      >
                        Saisie manuelle
                      </span>
                    )}
                    {detail.variable_rate_ca_source === "auto" && (
                      <span
                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-200"
                        data-testid="badge-ca-auto"
                      >
                        Auto
                      </span>
                    )}
                  </div>
                  {isAdmin && !isValidated && !editingCa && (
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingCa(true)}
                        data-testid="btn-edit-ca"
                      >
                        <Pencil size={11} className="mr-1" /> Corriger manuellement
                      </Button>
                      {detail.variable_rate_ca_source === "manual" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={resetManualCa}
                          disabled={saving}
                          className="text-emerald-700 hover:text-emerald-800"
                          data-testid="btn-reset-ca"
                        >
                          <X size={11} className="mr-1" /> Rétablir auto
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {editingCa ? (
                  <div className="col-span-2 flex items-end gap-2">
                    <div className="flex-1">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Laisser vide pour rétablir l'automatique"
                        value={caInput}
                        onChange={(e) => setCaInput(e.target.value)}
                        data-testid="input-ca"
                      />
                    </div>
                    <Button size="sm" onClick={saveCa} disabled={saving} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="btn-save-ca">
                      <Save size={11} className="mr-1" /> {saving ? "…" : "Enregistrer"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingCa(false); setCaInput(detail.variable_rate_ca_manual ?? ""); }}>
                      <X size={11} />
                    </Button>
                  </div>
                ) : (
                  <div className="col-span-2 font-mono text-sm text-zinc-900 text-right" data-testid="value-ca">
                    {money(detail.variable_rate_ca || 0)}
                  </div>
                )}

                <div className="col-span-2 border-t border-zinc-200 pt-2 mt-2 flex justify-between font-mono">
                  <span className="font-semibold text-zinc-700">Variable taux HT</span>
                  <span className="font-bold text-zinc-900" data-testid="detail-rate-total">{money(detail.variable_rate_amount)}</span>
                </div>
              </>
            ) : (
              <p className="col-span-2 text-xs text-zinc-500 italic">Non applicable — le mode variable configuré n&apos;est pas « pourcentage ».</p>
            )}
          </SectionCard>

          {/* Bloc 4 : Bonus exceptionnel de performance */}
          <SectionCard title="4. Bonus exceptionnel de performance" icon={<TrendingUp size={14} />} accent="#F59E0B">
            <div className="col-span-2 text-[11px] text-zinc-500 font-mono leading-relaxed">
              Bonus automatique : +5 % du forfait HT par dimanche/férié effectivement travaillé
              (règle métier). Bonus admin : saisie manuelle en sus (facultatif).
            </div>
            {/* Ligne bonus auto — visible dès qu'il y a au moins un dimanche/férié travaillé */}
            {(detail.dow_bonus_days || 0) > 0 && (
              <div
                className="col-span-2 flex items-center justify-between rounded-sm bg-amber-50 border border-amber-100 px-2.5 py-1.5"
                data-testid="row-bonus-auto"
              >
                <span className="text-xs text-amber-800 font-mono">
                  Bonus dimanche/férié auto&nbsp;·&nbsp;
                  <b>{detail.dow_bonus_days}</b> j&nbsp;× 5 % de {money(detail.fixed_ht_config || 0)}
                </span>
                <span className="font-mono text-sm text-amber-900 font-semibold" data-testid="value-bonus-auto">
                  {money(detail.bonus_auto || 0)}
                </span>
              </div>
            )}
            {isAdmin && !isValidated ? (
              editingBonus ? (
                <div className="col-span-2 flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Montant HT (ex. 250)"
                    value={bonusInput}
                    onChange={(e) => setBonusInput(e.target.value)}
                    className="max-w-[180px]"
                    data-testid="input-bonus"
                  />
                  <Button size="sm" onClick={saveBonus} disabled={savingBonus} className="bg-amber-500 hover:bg-amber-600" data-testid="btn-save-bonus">
                    <Save size={12} className="mr-1" /> {savingBonus ? "…" : "Enregistrer"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingBonus(false);
                      setBonusInput(detail.bonus_amount ? String(detail.bonus_amount) : "");
                    }}
                    data-testid="btn-cancel-bonus"
                  >
                    <X size={12} />
                  </Button>
                </div>
              ) : (
                <div className="col-span-2 flex items-center justify-between">
                  <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Bonus admin (saisie manuelle)</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-900" data-testid="value-bonus">
                      {money(detail.bonus_amount || 0)}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingBonus(true)}
                      data-testid="btn-edit-bonus"
                    >
                      <Pencil size={12} className="mr-1" /> {(detail.bonus_amount || 0) > 0 ? "Modifier" : "Ajouter"}
                    </Button>
                    {(detail.bonus_amount || 0) > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => { setBonusInput(""); await saveBonus(); }}
                        disabled={savingBonus}
                        data-testid="btn-remove-bonus"
                      >
                        <X size={12} />
                      </Button>
                    )}
                  </div>
                </div>
              )
            ) : (
              <div className="col-span-2 flex items-center justify-between">
                <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Bonus admin (saisie manuelle)</span>
                <span className="font-mono text-sm text-zinc-900" data-testid="value-bonus-readonly">
                  {money(detail.bonus_amount || 0)}
                </span>
              </div>
            )}
            <div className="col-span-2 border-t border-zinc-200 pt-2 mt-2 flex justify-between font-mono">
              <span className="font-semibold text-zinc-700">Total bonus HT</span>
              <span className="font-bold text-zinc-900" data-testid="detail-bonus-total">
                {money((Number(detail.bonus_auto) || 0) + (Number(detail.bonus_amount) || 0))}
              </span>
            </div>
          </SectionCard>

          {/* Bloc 5 : Réajustement sur périodes précédentes */}
          <SectionCard title="5. Réajustement de rémunération sur périodes précédentes" icon={<ArrowUpDown size={14} />} accent="#8B5CF6">
            <div className="col-span-2 text-[11px] text-zinc-500 font-mono leading-relaxed">
              Saisie admin. Choisissez « Manque à gagner » (ajouté) ou « Trop perçu » (soustrait), le montant en € et un motif obligatoire.
            </div>
            {isAdmin && !isValidated ? (
              editingAdj ? (
                <div className="col-span-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="radio-adj-manque">
                      <input
                        type="radio"
                        name="adj-type"
                        value="manque_a_gagner"
                        checked={adjType === "manque_a_gagner"}
                        onChange={() => setAdjType("manque_a_gagner")}
                        className="accent-emerald-600"
                      />
                      <ArrowUpCircle size={13} className="text-emerald-600" />
                      Manque à gagner (+)
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="radio-adj-trop">
                      <input
                        type="radio"
                        name="adj-type"
                        value="trop_percu"
                        checked={adjType === "trop_percu"}
                        onChange={() => setAdjType("trop_percu")}
                        className="accent-rose-600"
                      />
                      <ArrowDownCircle size={13} className="text-rose-600" />
                      Trop perçu (−)
                    </label>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[140px]">
                      <Label className="label-uppercase mb-1 block text-[10px]">Montant (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex. 150.00"
                        value={adjAmount}
                        onChange={(e) => setAdjAmount(e.target.value)}
                        data-testid="input-adj-amount"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1 block text-[10px]">Motif du réajustement <span className="text-red-500">*</span></Label>
                    <textarea
                      value={adjComment}
                      onChange={(e) => setAdjComment(e.target.value)}
                      rows={2}
                      maxLength={1000}
                      placeholder="Ex. Correction du trop perçu de juin 2026 (jour férié comptabilisé à tort)"
                      className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]/40"
                      data-testid="input-adj-comment"
                    />
                    <p className="text-[10px] text-zinc-400 mt-1">{adjComment.length}/1000</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={saveAdjustment} disabled={savingAdj} className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="btn-save-adj">
                      <Save size={12} className="mr-1" /> {savingAdj ? "…" : "Enregistrer"}
                    </Button>
                    {(detail.adjustment_amount || 0) > 0 && (
                      <Button size="sm" variant="outline" onClick={removeAdjustment} disabled={savingAdj} data-testid="btn-remove-adj">
                        <X size={12} className="mr-1" /> Retirer
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingAdj(false);
                        setAdjAmount(detail.adjustment_amount ? String(detail.adjustment_amount) : "");
                        setAdjType(detail.adjustment_type || "manque_a_gagner");
                        setAdjComment(detail.adjustment_comment || "");
                      }}
                      data-testid="btn-cancel-adj"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="col-span-2 flex items-center justify-between">
                  <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Réajustement</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-900" data-testid="value-adj">
                      {(detail.adjustment_amount || 0) > 0
                        ? `${detail.adjustment_type === "trop_percu" ? "− " : "+ "}${money(detail.adjustment_amount)}`
                        : money(0)}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setEditingAdj(true)} data-testid="btn-edit-adj">
                      <Pencil size={12} className="mr-1" /> {(detail.adjustment_amount || 0) > 0 ? "Modifier" : "Ajouter"}
                    </Button>
                  </div>
                </div>
              )
            ) : (
              <div className="col-span-2 flex items-center justify-between">
                <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Réajustement</span>
                <span className="font-mono text-sm text-zinc-900" data-testid="value-adj-readonly">
                  {(detail.adjustment_amount || 0) > 0
                    ? `${detail.adjustment_type === "trop_percu" ? "− " : "+ "}${money(detail.adjustment_amount)}`
                    : money(0)}
                </span>
              </div>
            )}
            {(detail.adjustment_amount || 0) > 0 && detail.adjustment_comment && !editingAdj && (
              <div className="col-span-2 bg-zinc-50 border border-zinc-200 rounded-sm p-2 text-xs italic text-zinc-700" data-testid="value-adj-comment">
                <span className="font-semibold not-italic">Motif :</span> {detail.adjustment_comment}
              </div>
            )}
            <div className="col-span-2 border-t border-zinc-200 pt-2 mt-2 flex justify-between font-mono">
              <span className="font-semibold text-zinc-700">Impact sur total HT</span>
              <span className={`font-bold ${(detail.adjustment_signed || 0) < 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="detail-adj-total">
                {(detail.adjustment_signed || 0) >= 0 ? "+ " : "− "}{money(Math.abs(detail.adjustment_signed || 0))}
              </span>
            </div>
          </SectionCard>

          {/* Bloc 6 : Avance / acompte versé */}
          <SectionCard title="6. Avance / acompte versé" icon={<BanknoteArrowDown size={14} />} accent="#DB2777">
            <div className="col-span-2 text-[11px] text-zinc-500 font-mono leading-relaxed">
              Saisie admin. Le montant versé au collaborateur en cours de période est retranché de sa rémunération finale.
            </div>
            {isAdmin && !isValidated ? (
              editingAdv ? (
                <div className="col-span-2 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="label-uppercase mb-1 block text-[10px]">Montant (€) <span className="text-red-500">*</span></Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex. 500.00"
                        value={advAmount}
                        onChange={(e) => setAdvAmount(e.target.value)}
                        data-testid="input-adv-amount"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1 block text-[10px]">Date <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={advDate}
                        onChange={(e) => setAdvDate(e.target.value)}
                        data-testid="input-adv-date"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1 block text-[10px]">Mode <span className="text-red-500">*</span></Label>
                      <SearchableSelect
                        value={advMethod}
                        onChange={(v) => setAdvMethod(v)}
                        options={[
                          { value: "virement", label: "Virement" },
                          { value: "especes", label: "Espèces" },
                          { value: "cheque", label: "Chèque" },
                          { value: "autre", label: "Autre" },
                        ]}
                        placeholder="Choisir"
                        searchPlaceholder="Rechercher…"
                        data-testid="input-adv-method"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={saveAdvance} disabled={savingAdv} className="bg-[#DB2777] hover:bg-[#BE185D]" data-testid="btn-save-adv">
                      <Save size={12} className="mr-1" /> {savingAdv ? "…" : "Enregistrer"}
                    </Button>
                    {(detail.advance_amount || 0) > 0 && (
                      <Button size="sm" variant="outline" onClick={removeAdvance} disabled={savingAdv} data-testid="btn-remove-adv">
                        <X size={12} className="mr-1" /> Retirer
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingAdv(false);
                        setAdvAmount(detail.advance_amount ? String(detail.advance_amount) : "");
                        setAdvDate(detail.advance_date || "");
                        setAdvMethod(detail.advance_method || "virement");
                      }}
                      data-testid="btn-cancel-adv"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="col-span-2 flex items-center justify-between">
                  <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Avance versée</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-900" data-testid="value-adv">
                      {(detail.advance_amount || 0) > 0 ? `− ${money(detail.advance_amount)}` : money(0)}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setEditingAdv(true)} data-testid="btn-edit-adv">
                      <Pencil size={12} className="mr-1" /> {(detail.advance_amount || 0) > 0 ? "Modifier" : "Ajouter"}
                    </Button>
                  </div>
                </div>
              )
            ) : (
              <div className="col-span-2 flex items-center justify-between">
                <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Avance versée</span>
                <span className="font-mono text-sm text-zinc-900" data-testid="value-adv-readonly">
                  {(detail.advance_amount || 0) > 0 ? `− ${money(detail.advance_amount)}` : money(0)}
                </span>
              </div>
            )}
            {(detail.advance_amount || 0) > 0 && !editingAdv && (
              <div className="col-span-2 grid grid-cols-2 gap-2 text-xs text-zinc-700" data-testid="value-adv-meta">
                <div>
                  <span className="font-mono uppercase tracking-wider text-zinc-500 text-[10px]">Date : </span>
                  <span className="font-mono">{detail.advance_date || "—"}</span>
                </div>
                <div>
                  <span className="font-mono uppercase tracking-wider text-zinc-500 text-[10px]">Mode : </span>
                  <span className="font-mono">
                    {({ virement: "Virement", especes: "Espèces", cheque: "Chèque", autre: "Autre" }[detail.advance_method]) || "—"}
                  </span>
                </div>
              </div>
            )}
            <div className="col-span-2 border-t border-zinc-200 pt-2 mt-2 flex justify-between font-mono">
              <span className="font-semibold text-zinc-700">Impact sur total HT</span>
              <span className="font-bold text-rose-700" data-testid="detail-adv-total">
                {(detail.advance_amount || 0) > 0 ? `− ${money(detail.advance_amount)}` : money(0)}
              </span>
            </div>
          </SectionCard>

          {/* Total */}
          <div className="bg-[#0055FF] text-white rounded-sm p-4 flex items-center justify-between">
            <span className="font-semibold text-sm uppercase tracking-wider">Total rémunération HT</span>
            <span className="font-mono text-2xl font-bold" data-testid="detail-total-ht">{money(detail.total_ht)}</span>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => onDownloadPdf(detail.collaborateur_id, month)} data-testid="btn-modal-pdf">
            <Download size={13} className="mr-1" /> Télécharger PDF
          </Button>
          {isAdmin && !isValidated && (
            <Button
              onClick={validate}
              disabled={confirming || (detail.total_ht || 0) <= 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-validate"
            >
              <CheckCircle2 size={13} className="mr-1" /> {confirming ? "Validation…" : "Valider le relevé"}
            </Button>
          )}
          {isAdmin && isValidated && (
            <Button variant="outline" onClick={() => setReopenOpen(true)} data-testid="btn-reopen">
              <RotateCcw size={13} className="mr-1" /> Rouvrir
            </Button>
          )}
          <Button variant="outline" onClick={onClose} data-testid="btn-close-modal">Fermer</Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <AlertDialogContent data-testid="reopen-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rouvrir ce relevé ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le collaborateur ne pourra plus le consulter tant qu&apos;il n&apos;aura pas été revalidé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-reopen-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={reopen} data-testid="btn-reopen-confirm">
              Rouvrir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

function SectionCard({ title, icon, accent, children }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4 bg-white">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-7 w-7 rounded-sm text-white inline-flex items-center justify-center" style={{ backgroundColor: accent }}>
          {icon}
        </span>
        <h4 className="text-sm font-semibold text-zinc-900">{title}</h4>
      </div>
      <div className="flex flex-col gap-1 md:grid md:grid-cols-2 md:gap-2 text-sm">{children}</div>
    </div>
  );
}

function KV({ label, value, tid }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-1" data-testid={tid}>
      <span className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider leading-snug min-w-0 break-words flex-1">{label}</span>
      <span className="font-mono text-sm text-zinc-900 shrink-0 whitespace-nowrap">{value}</span>
    </div>
  );
}
